import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../store/AuthContext';
import { authRegister, authRequestCode, authVerifyCode, uploadPhotos } from '../lib/api';
import SEOHead from '../components/SEOHead';
import {
  Mail, User, Phone, Building2, MapPin, FileText, Camera,
  CheckCircle2, Loader2, KeyRound, ChevronRight, ChevronLeft, X, Plus,
} from 'lucide-react';

type Step = 1 | 2 | 3 | 4 | 5;

interface PhotoFile {
  file: File;
  preview: string;
  caption: string;
}

export default function RegisterPage() {
  const navigate = useNavigate();
  const { setUser } = useAuth();

  const [step, setStep] = useState<Step>(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Step 1
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');

  // Step 2
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [btwNumber, setBtwNumber] = useState('');

  // Step 3
  const [notes, setNotes] = useState('');

  // Step 4
  const [photos, setPhotos] = useState<PhotoFile[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Step 5 — verification
  const [code, setCode] = useState('');
  const [verified, setVerified] = useState(false);

  const handleAddPhotos = useCallback((files: FileList | null) => {
    if (!files) return;
    const remaining = 5 - photos.length;
    const toAdd = Array.from(files).slice(0, remaining);
    const newPhotos: PhotoFile[] = toAdd.map(file => ({
      file,
      preview: URL.createObjectURL(file),
      caption: '',
    }));
    setPhotos(prev => [...prev, ...newPhotos]);
  }, [photos.length]);

  const handleRemovePhoto = (index: number) => {
    setPhotos(prev => {
      const copy = [...prev];
      URL.revokeObjectURL(copy[index].preview);
      copy.splice(index, 1);
      return copy;
    });
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    handleAddPhotos(e.dataTransfer.files);
  }, [handleAddPhotos]);

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleRegister = async () => {
    setLoading(true);
    setError('');
    try {
      // Register user
      await authRegister({
        email: email.trim().toLowerCase(),
        name: name.trim(),
        phone: phone.trim() || undefined,
        company_name: companyName.trim() || undefined,
        btw_number: btwNumber.trim() || undefined,
        address: address.trim() || undefined,
        city: city.trim() || undefined,
        postal_code: postalCode.trim() || undefined,
        notes: notes.trim() || undefined,
      });

      // Request code (already sent by register, but ensure we have one)
      await authRequestCode(email.trim().toLowerCase());

      setStep(5);
    } catch (err: unknown) {
      const msg = (err as { message?: string })?.message || 'Registratie mislukt. Probeer opnieuw.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (code.length < 6) return;
    setLoading(true);
    setError('');
    try {
      const { user } = await authVerifyCode(email.trim().toLowerCase(), code.trim());

      // Upload photos if any
      if (photos.length > 0) {
        const photoData = await Promise.all(
          photos.map(async p => ({
            data: await fileToBase64(p.file),
            caption: p.caption || undefined,
          }))
        );
        try {
          await uploadPhotos(photoData);
        } catch {
          // Non-blocking: photos upload may fail, user is still registered
        }
      }

      setUser(user);
      setVerified(true);
      setTimeout(() => {
        navigate(user.role === 'customer' ? '/dashboard' : '/admin');
      }, 1500);
    } catch (err: unknown) {
      setError((err as { message?: string })?.message || 'Ongeldige code. Probeer opnieuw.');
    } finally {
      setLoading(false);
    }
  };

  const canProceed = (s: Step): boolean => {
    switch (s) {
      case 1: return !!email.trim() && !!name.trim() && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
      case 2: return true; // optional fields
      case 3: return true; // optional
      case 4: return true; // optional
      case 5: return code.length === 6;
      default: return false;
    }
  };

  const stepLabels = [
    'Contact',
    'Adres',
    'Opmerkingen',
    "Foto's",
    'Bevestigen',
  ];

  return (
    <div className="pt-20 md:pt-24 min-h-screen flex items-center justify-center bg-[#faf7f3]">
      <SEOHead page="register" />
      <div className="w-full max-w-lg mx-4 mb-12">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">

          {/* Header */}
          <div className="bg-[#1a1a1a] text-white px-6 py-5">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-[#c47a3a] rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">G</span>
              </div>
              <div>
                <h1 className="text-lg font-bold">Account aanmaken</h1>
                <p className="text-xs text-gray-400">Gevelsteenbedrijf.be</p>
              </div>
            </div>

            {/* Progress bar */}
            <div className="flex items-center gap-1 mt-2">
              {stepLabels.map((label, i) => (
                <div key={i} className="flex-1">
                  <div className={`h-1 rounded-full transition-colors ${
                    i + 1 <= step ? 'bg-[#c47a3a]' : 'bg-white/20'
                  }`} />
                  <p className={`text-[9px] mt-1 text-center ${
                    i + 1 === step ? 'text-[#c47a3a] font-semibold' : 'text-gray-500'
                  }`}>{label}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="p-6">
            {error && (
              <div className="mb-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
                {error}
              </div>
            )}

            {/* Step 1: Contact */}
            {step === 1 && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#4a4a4a] mb-1">
                    Naam <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a7e74]" />
                    <input
                      type="text"
                      required
                      value={name}
                      onChange={e => { setName(e.target.value); setError(''); }}
                      placeholder="Uw volledige naam"
                      className="w-full pl-10 pr-4 py-3 bg-[#faf7f3] border border-gray-200 rounded-lg focus:outline-none focus:border-[#c47a3a] text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#4a4a4a] mb-1">
                    E-mailadres <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a7e74]" />
                    <input
                      type="email"
                      required
                      value={email}
                      onChange={e => { setEmail(e.target.value); setError(''); }}
                      placeholder="naam@bedrijf.be"
                      className="w-full pl-10 pr-4 py-3 bg-[#faf7f3] border border-gray-200 rounded-lg focus:outline-none focus:border-[#c47a3a] text-sm"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-[#4a4a4a] mb-1">Telefoonnummer</label>
                  <div className="relative">
                    <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a7e74]" />
                    <input
                      type="tel"
                      value={phone}
                      onChange={e => setPhone(e.target.value)}
                      placeholder="+32 476 12 34 56"
                      className="w-full pl-10 pr-4 py-3 bg-[#faf7f3] border border-gray-200 rounded-lg focus:outline-none focus:border-[#c47a3a] text-sm"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Address + Company */}
            {step === 2 && (
              <div className="space-y-4">
                <h3 className="text-sm font-bold text-[#4a4a4a] flex items-center gap-2">
                  <MapPin size={16} className="text-[#c47a3a]" /> Adresgegevens
                </h3>
                <div>
                  <label className="block text-sm font-medium text-[#4a4a4a] mb-1">Straat + huisnummer</label>
                  <input
                    type="text"
                    value={address}
                    onChange={e => setAddress(e.target.value)}
                    placeholder="Kerkstraat 42"
                    className="w-full px-4 py-3 bg-[#faf7f3] border border-gray-200 rounded-lg focus:outline-none focus:border-[#c47a3a] text-sm"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-[#4a4a4a] mb-1">Postcode</label>
                    <input
                      type="text"
                      value={postalCode}
                      onChange={e => setPostalCode(e.target.value)}
                      placeholder="2000"
                      className="w-full px-4 py-3 bg-[#faf7f3] border border-gray-200 rounded-lg focus:outline-none focus:border-[#c47a3a] text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#4a4a4a] mb-1">Stad</label>
                    <input
                      type="text"
                      value={city}
                      onChange={e => setCity(e.target.value)}
                      placeholder="Antwerpen"
                      className="w-full px-4 py-3 bg-[#faf7f3] border border-gray-200 rounded-lg focus:outline-none focus:border-[#c47a3a] text-sm"
                    />
                  </div>
                </div>

                <div className="border-t border-gray-100 pt-4 mt-4">
                  <h3 className="text-sm font-bold text-[#4a4a4a] flex items-center gap-2 mb-3">
                    <Building2 size={16} className="text-[#c47a3a]" /> Bedrijfsgegevens <span className="text-xs font-normal text-[#8a7e74]">(optioneel)</span>
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-[#4a4a4a] mb-1">Bedrijfsnaam</label>
                      <input
                        type="text"
                        value={companyName}
                        onChange={e => setCompanyName(e.target.value)}
                        placeholder="Bedrijf NV"
                        className="w-full px-4 py-3 bg-[#faf7f3] border border-gray-200 rounded-lg focus:outline-none focus:border-[#c47a3a] text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#4a4a4a] mb-1">BTW-nummer</label>
                      <input
                        type="text"
                        value={btwNumber}
                        onChange={e => setBtwNumber(e.target.value)}
                        placeholder="BE0123.456.789"
                        className="w-full px-4 py-3 bg-[#faf7f3] border border-gray-200 rounded-lg focus:outline-none focus:border-[#c47a3a] text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Notes */}
            {step === 3 && (
              <div className="space-y-4">
                <h3 className="text-sm font-bold text-[#4a4a4a] flex items-center gap-2">
                  <FileText size={16} className="text-[#c47a3a]" /> Wat zoekt u?
                </h3>
                <p className="text-xs text-[#8a7e74]">
                  Vertel ons kort over uw project. Welke stijl, kleur of afwerking heeft u in gedachten?
                </p>
                <div className="relative">
                  <textarea
                    value={notes}
                    onChange={e => setNotes(e.target.value.slice(0, 500))}
                    placeholder="Bijv. Ik zoek rode baksteenstrips voor een renovatieproject van 80m². Voorkeur voor een klassieke uitstraling..."
                    rows={5}
                    className="w-full px-4 py-3 bg-[#faf7f3] border border-gray-200 rounded-lg focus:outline-none focus:border-[#c47a3a] text-sm resize-none"
                  />
                  <span className={`absolute bottom-3 right-3 text-[10px] ${notes.length > 450 ? 'text-red-400' : 'text-[#8a7e74]'}`}>
                    {notes.length}/500
                  </span>
                </div>
              </div>
            )}

            {/* Step 4: Photos */}
            {step === 4 && (
              <div className="space-y-4">
                <h3 className="text-sm font-bold text-[#4a4a4a] flex items-center gap-2">
                  <Camera size={16} className="text-[#c47a3a]" /> Foto's van uw project
                </h3>
                <p className="text-xs text-[#8a7e74]">
                  Upload tot 5 foto's van uw woning of project. Dit helpt ons u beter te adviseren. (Optioneel)
                </p>

                {/* Drop zone */}
                {photos.length < 5 && (
                  <div
                    onDragOver={e => e.preventDefault()}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-gray-200 rounded-xl p-6 text-center cursor-pointer hover:border-[#c47a3a] transition-colors"
                  >
                    <Plus size={24} className="text-[#8a7e74] mx-auto mb-2" />
                    <p className="text-sm font-medium text-[#4a4a4a]">Klik of sleep foto's hierheen</p>
                    <p className="text-xs text-[#8a7e74] mt-1">JPG, PNG — max 5 foto's</p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      multiple
                      className="hidden"
                      onChange={e => handleAddPhotos(e.target.files)}
                    />
                  </div>
                )}

                {/* Photo previews */}
                {photos.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {photos.map((photo, i) => (
                      <div key={i} className="relative group">
                        <img
                          src={photo.preview}
                          alt={`Foto ${i + 1}`}
                          className="w-full h-24 object-cover rounded-lg border border-gray-200"
                        />
                        <button
                          onClick={() => handleRemovePhoto(i)}
                          className="absolute top-1 right-1 w-6 h-6 bg-black/60 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X size={12} className="text-white" />
                        </button>
                        <input
                          type="text"
                          value={photo.caption}
                          onChange={e => {
                            const copy = [...photos];
                            copy[i] = { ...copy[i], caption: e.target.value };
                            setPhotos(copy);
                          }}
                          placeholder="Bijschrift..."
                          className="w-full mt-1 px-2 py-1 bg-[#faf7f3] border border-gray-200 rounded text-[10px] focus:outline-none focus:border-[#c47a3a]"
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Step 5: Verify */}
            {step === 5 && !verified && (
              <div className="space-y-4">
                <div className="text-center mb-2">
                  <div className="w-12 h-12 bg-[#c47a3a]/10 rounded-full mx-auto flex items-center justify-center mb-3">
                    <KeyRound size={20} className="text-[#c47a3a]" />
                  </div>
                  <h3 className="text-base font-bold text-[#1a1a1a]">Bevestig uw e-mailadres</h3>
                  <p className="text-xs text-[#8a7e74] mt-1">
                    We hebben een 6-cijferige code gestuurd naar <strong>{email}</strong>
                  </p>
                </div>
                <div>
                  <input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    value={code}
                    onChange={e => { setCode(e.target.value.replace(/\D/g, '')); setError(''); }}
                    placeholder="123456"
                    className="w-full px-4 py-4 bg-[#faf7f3] border border-gray-200 rounded-lg text-center tracking-[0.3em] font-mono text-xl focus:outline-none focus:border-[#c47a3a]"
                    autoFocus
                  />
                  <p className="text-[10px] text-[#8a7e74] mt-2 text-center">
                    Tip: gebruik code <strong>123456</strong> voor de testomgeving
                  </p>
                </div>
                <button
                  onClick={handleVerify}
                  disabled={loading || code.length < 6}
                  className="w-full flex items-center justify-center gap-2 py-3 bg-[#c47a3a] text-white text-sm font-semibold rounded-lg hover:bg-[#b06a2a] transition-colors disabled:opacity-50"
                >
                  {loading ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                  {loading ? 'Verifiëren...' : 'Account bevestigen'}
                </button>
              </div>
            )}

            {/* Success */}
            {step === 5 && verified && (
              <div className="flex flex-col items-center py-8 gap-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                  <CheckCircle2 size={32} className="text-green-500" />
                </div>
                <h3 className="text-lg font-bold text-[#1a1a1a]">Welkom bij Gevelsteenbedrijf!</h3>
                <p className="text-sm text-[#8a7e74]">Uw account is aangemaakt. U wordt doorgestuurd...</p>
              </div>
            )}

            {/* Navigation */}
            {step < 5 && (
              <div className="flex items-center justify-between mt-6 pt-4 border-t border-gray-100">
                {step > 1 ? (
                  <button
                    onClick={() => setStep((step - 1) as Step)}
                    className="flex items-center gap-1 text-sm text-[#8a7e74] hover:text-[#4a4a4a] transition-colors"
                  >
                    <ChevronLeft size={16} /> Vorige
                  </button>
                ) : (
                  <div />
                )}

                {step < 4 ? (
                  <button
                    onClick={() => setStep((step + 1) as Step)}
                    disabled={!canProceed(step)}
                    className="flex items-center gap-1 px-5 py-2.5 bg-[#c47a3a] text-white text-sm font-semibold rounded-lg hover:bg-[#b06a2a] transition-colors disabled:opacity-50"
                  >
                    Volgende <ChevronRight size={16} />
                  </button>
                ) : (
                  <button
                    onClick={handleRegister}
                    disabled={loading}
                    className="flex items-center gap-2 px-5 py-2.5 bg-[#c47a3a] text-white text-sm font-semibold rounded-lg hover:bg-[#b06a2a] transition-colors disabled:opacity-50"
                  >
                    {loading ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
                    {loading ? 'Registreren...' : 'Account aanmaken'}
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Login link */}
        {step < 5 && (
          <p className="text-center text-sm text-[#8a7e74] mt-4">
            Heeft u al een account?{' '}
            <a href="/login" className="text-[#c47a3a] font-medium hover:underline">Inloggen</a>
          </p>
        )}
      </div>
    </div>
  );
}
